import { useState, useEffect, useCallback, useRef } from "react";
import { onConnectivityChange } from "@/lib/api";

// Shared resource loader with safe offline semantics.
// loader: async () => ({ data, stale }). Must use apiGet so it only throws on a
// cold-start network failure (no previously cached value).
// States: loading | loaded (with stale flag) | unavailable (cold-start offline).
export function useResource(loader, deps = []) {
  const [state, setState] = useState({ data: null, loaded: false, stale: false, unavailable: false, loading: true });
  const loadedRef = useRef(false);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data, stale } = await loaderRef.current();
      loadedRef.current = true;
      setState({ data, loaded: true, stale: !!stale, unavailable: false, loading: false });
    } catch (e) {
      if (loadedRef.current) {
        // We had real data before — keep it, just mark potentially stale.
        setState((s) => ({ ...s, loading: false, stale: true }));
      } else {
        // Cold start with no data ever loaded in this runtime.
        setState({ data: null, loaded: false, stale: false, unavailable: true, loading: false });
      }
    }
  }, []);

  // Load on mount and when deps change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, deps);

  // Auto-refresh authoritative data when the API becomes reachable again.
  useEffect(() => onConnectivityChange((v) => { if (v) reload(); }), [reload]);

  // Also revalidate on the browser 'online' hint.
  useEffect(() => {
    const onOnline = () => reload();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [reload]);

  return { ...state, reload };
}
